import { queryKeys } from '../keys'
import { DEFAULT_RETRY_MUTATION } from '../config'
import type { QueryClient } from '@tanstack/react-query'
import type { GetUserProfileResponse } from '@/types/user'
import { userService } from '@/services'
import { router } from '@/main'

const mutatePatchAccount = (queryClient: QueryClient) => {
  return queryClient.setMutationDefaults(queryKeys.user.patch(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof userService.patchProfile>[0]) =>
      userService.patchProfile(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user.profile() })
    },
    onMutate: async (payload) => {
      const profileQueryKey = queryKeys.user.profile()
      await queryClient.cancelQueries({ queryKey: profileQueryKey })

      const previousProfile = queryClient.getQueryData(
        profileQueryKey,
      ) as GetUserProfileResponse

      queryClient.setQueryData(
        profileQueryKey,
        (old: GetUserProfileResponse) => ({
          ...old,
          data: {
            ...old.data,
            admin: {
              ...old.data.admin,
              first_name:
                payload.organization_admin?.first_name ??
                old.data.admin.first_name,
              last_name:
                payload.organization_admin?.last_name ??
                old.data.admin.last_name,
              phone_number:
                payload.organization_admin?.phone_number ??
                old.data.admin.phone_number,
              organization: {
                ...old.data.admin.organization,
                ...payload.organization_admin?.organization_attributes,
              },
            },
          },
        }),
      )

      return { previousProfile }
    },
    onError: (_err, _newData, context) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(
          queryKeys.user.profile(),
          context.previousProfile,
        )
      }
    },
  })
}

const mutateDeleteAccountRequestVerification = (queryClient: QueryClient) => {
  return queryClient.setMutationDefaults(queryKeys.user.verification(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (
      payload: Parameters<
        typeof userService.deleteAccountRequestVerification
      >[0],
    ) => userService.deleteAccountRequestVerification(payload),
  })
}
const mutateDeleteAccount = (queryClient: QueryClient) => {
  return queryClient.setMutationDefaults(queryKeys.user.delete(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof userService.deleteAccount>[0]) =>
      userService.deleteAccount(payload),
    onSuccess: () => {
      router.navigate({ to: '/auth/logout', replace: true })
    },
  })
}

const mutateSendVerificationEmail = (queryClient: QueryClient) => {
  return queryClient.setMutationDefaults(
    queryKeys.user.sendVerificationEmail(),
    {
      retry: DEFAULT_RETRY_MUTATION,
      mutationFn: () => userService.sendVerificationEmail(),
    },
  )
}

const mutateVerifyEmail = (queryClient: QueryClient) => {
  return queryClient.setMutationDefaults(queryKeys.user.verifyEmail(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof userService.verifyEmail>[0]) =>
      userService.verifyEmail(payload),
    onSuccess: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.user.profile() })
      queryClient.invalidateQueries({ queryKey: queryKeys.user.profile() })
    },
  })
}

const mutateChangePassword = (queryClient: QueryClient) => {
  return queryClient.setMutationDefaults(queryKeys.user.changePassword(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof userService.changePassword>[0]) =>
      userService.changePassword(payload),
  })
}

export function registerUserMutations(queryClient: QueryClient) {
  mutatePatchAccount(queryClient)
  mutateDeleteAccount(queryClient)
  mutateDeleteAccountRequestVerification(queryClient)
  mutateSendVerificationEmail(queryClient)
  mutateVerifyEmail(queryClient)
  mutateChangePassword(queryClient)
}
